import React, { FC, useEffect, useMemo, useState } from 'react';
import { Icon, Label, LoadingCircle, TextBadge, Typography } from '@packages/ui/shared';
import {
    DocumentVersion,
    PublicationForm,
    PublicationStatusEnum,
    StisPublicationAction,
    statusVariants,
} from '@web/vin-sessions/types';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { PublicationFormDetailViewAside } from './publication-detail-view-aside';
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from '@web/components/select';
import { UsePublicationFormReturnType } from './hooks/use-publication-form';
import { WorksheetUser } from '@web/administration/views/worksheets/components/worksheets-user';
import useWorksheetFormTranslation from '@web/administration/views/worksheets/hooks/use-worksheet-form-translation';
import { View } from 'react-native';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { docsService } from '@web/services/singletons';
import { handleVinSessionError } from '@web/vin-sessions/utils/vinSessionUtils';
import { isRichTextEmpty } from './description.utils';
import { withVersion } from './version.utils';

interface Props {
    checkRequiredFields: () => void;
    publicationInfo: PublicationForm;
    actions?: UsePublicationFormReturnType['actions'];
    statusOfPublication: {
        isArchivedPublication: boolean;
        isPublishedPublication: boolean;
        isDraftPublicaiton: boolean;
        isInReviewPublication: boolean;
    };
    creatorUser: {
        firstName: string;
        lastName: string;
        name: string;
    } | null;
    updatedUser: {
        firstName: string;
        lastName: string;
        name: string;
    } | null;
    parentPublications: DocumentVersion[];
    publishBlockedMessage?: string | null;
}

interface sidebarFilter {
    name: string;
    onClick: () => void;
}

const useStyles = createUseStyles({
    flexGap: {
        gap: 20,
    },
    cursorPointer: {
        cursor: 'pointer',
    },
    tooltip: {
        marginTop: 5,
        width: 272,
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '20px',
            maxWidth: 272,
            padding: '3px 8px',
            textAlign: 'left',
            width: 272,
        },
    },
    versionMeta: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
    },
    customTooltip: {
        fontSize: 16,
        padding: '8px 12px',
        textAlign: 'center',
        marginTop: '-4px !important',
    },
    icons: {
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 4,
        '&:hover': {
            backgroundColor: '#dee6ef',
        },
    },
});

const VersionStatus: FC<{ status?: string }> = ({ status }) =>
    status ? <TextBadge variant={statusVariants[status]}>{status}</TextBadge> : null;

export const PublicationFormRightSidebar = ({
    publicationInfo,
    actions,
    statusOfPublication,
    checkRequiredFields,
    creatorUser,
    updatedUser,
    parentPublications,
    publishBlockedMessage,
}: Props) => {
    const { publicationFormId } = useParams();
    const selectedTabHeadStyles = {
        borderBottomColor: colors.blueOne,
        color: colors.blueOne,
        borderBottomWidth: 2,
        paddingBottom: 24,
    };

    const { dataTranslate } = useWorksheetFormTranslation();
    const { t } = useTranslation();
    const [selectedFilter, setSelectedFilter] = React.useState('Details');
    const classes = useStyles();
    const PUBLICATION_TOOLTIP = '';
    const createdTime = publicationInfo.createdTimestamp;

    // Move getCurrentFormattedDate above its first usage
    const getCurrentFormattedDate = (date: string) => {
        const now = new Date(date);
        return (
            now.toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric',
            }) +
            ' at ' +
            now.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            })
        );
    };
    const updatedTime = publicationInfo.lastUpdated ? getCurrentFormattedDate(publicationInfo.lastUpdated) : '';
    const [canAddPublishedStatus, setCanAddPublishedStatus] = useState<boolean>(false);

    const sidebarFilters: sidebarFilter[] = React.useMemo(
        () => [
            {
                name: t('stisPublications:sideBarFilters.details', 'Details'),
                onClick: () => setSelectedFilter('Details'),
            },
            // {
            //     name: t('stisPublications:sideBarFilters.activity', 'Activity'),
            //     onClick: () => setSelectedFilter('Activity'),
            // },
        ],
        []
    );

    const statusActions = {
        draft: t('stisPublications:statusActions.draft', 'Draft'),
        published: t('stisPublications:statusActions.published', 'Published'),
        archived: t('stisPublications:statusActions.archived', 'Archived'),
        inReview: t('stisPublications:statusActions.inReview', 'In Review'),
    };

    const getPublicationVersions = async (docId: string) => {
        try {
            const response = await docsService.getPublicationVersions(docId);
            return response.data || [];
        } catch (error) {
            handleVinSessionError('Fetching Versions Error', error);
            return [];
        }
    };

    useEffect(() => {
        if (publicationInfo.status == PublicationStatusEnum.Published) checkRequiredFields();
    }, [publicationInfo.status]);

    const statusActionMenu = useMemo(() => {
        const menu: { label: string; value: PublicationStatusEnum }[] = [];
        const draft = { label: statusActions.draft, value: PublicationStatusEnum.Draft };
        const published = { label: statusActions.published, value: PublicationStatusEnum.Published };
        const archived = { label: statusActions.archived, value: PublicationStatusEnum.Archived };
        const inReview = { label: statusActions.inReview, value: PublicationStatusEnum.InReview };

        const { isArchivedPublication, isPublishedPublication, isInReviewPublication } = statusOfPublication;

        if (!publicationFormId) {
            menu.push(draft);
            if (canAddPublishedStatus) menu.push(published);
        } else {
            if (isArchivedPublication) {
                menu.push(archived, draft, published, inReview);
            } else if (isPublishedPublication) {
                menu.push(published, draft, archived, inReview);
            } else if (isInReviewPublication) {
                menu.push(inReview, draft, published, archived);
            } else {
                menu.push(draft, published, archived, inReview);
            }
        }
        return menu;
    }, [
        statusActions.draft,
        statusActions.published,
        statusActions.archived,
        statusActions.inReview,
        canAddPublishedStatus,
        statusOfPublication,
        publicationFormId,
    ]);

    useEffect(() => {
        if (
            publicationInfo.title !== 'Untitled' &&
            publicationInfo?.publicationType &&
            publicationInfo?.publicationType.id !== '' &&
            publicationInfo.docCode != '' &&
            publicationInfo.docCode !== null &&
            publicationInfo.visibilityLevel &&
            publicationInfo.visibilityLevel.label != '' &&
            publicationInfo.url != '' &&
            !isRichTextEmpty(publicationInfo.description)
        )
            setCanAddPublishedStatus(true);
        else setCanAddPublishedStatus(false);
    }, [publicationInfo]);

    return (
        <PublicationFormDetailViewAside
            filters={
                <div
                    className={`d-flex flex-row ${classes.flexGap} mb-4 flex-wrap`}
                    style={{
                        borderBottom: `1px solid ${colors.grayThree}`,
                        width: '100%',
                        padding: '24px 24px 0 24px',
                    }}
                >
                    {sidebarFilters.map((filter: sidebarFilter) => (
                        <div key={filter.name} className={`${classes.cursorPointer} d-flex`} onClick={filter.onClick}>
                            <Typography
                                style={selectedFilter === filter.name ? selectedTabHeadStyles : {}}
                                color="grayFive"
                                variant="h6"
                            >
                                {filter.name}
                            </Typography>
                        </div>
                    ))}
                </div>
            }
        >
            <Row className="mb-4">
                <Col>
                    <div className="d-flex align-items-center">
                        <div className="d-flex align-items-center">
                            <Label
                                style={{
                                    fontWeight: '600',
                                    marginTop: 8,
                                    fontSize: 18,
                                    lineHeight: 24,
                                    letterSpacing: 0,
                                }}
                            >
                                {t('stisPublications:sideBarHeaders.status', 'Status')}
                            </Label>
                        </div>
                        <OverlayTrigger
                            key={'info'}
                            placement="bottom"
                            trigger={['hover', 'focus']}
                            overlay={
                                <Tooltip className={classes.tooltip} id={'info-tool-tip'}>
                                    {t('stisPublications:toolTipMessage', PUBLICATION_TOOLTIP)}
                                </Tooltip>
                            }
                        >
                            <Icon name="info" style={{ marginLeft: 10 }} color="grayFive" />
                        </OverlayTrigger>
                    </div>
                    <div className="mb-6">
                        {!actions?.isStatusTypeLoading ? (
                            <>
                                <Select
                                    value={publicationInfo.status}
                                    options={statusActionMenu.map((status) => ({
                                        title: status.label,
                                        value: status.value,
                                        disabled: Boolean(
                                            publishBlockedMessage && status.value === PublicationStatusEnum.Published
                                        ),
                                    }))}
                                    onChange={(event) => actions?.handleChangeStatusType(event.currentTarget.value)}
                                />
                                {publishBlockedMessage && (
                                    <Typography variant="caption" color="graySix" style={{ marginTop: 8 }}>
                                        {publishBlockedMessage}
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <LoadingCircle style={{ position: 'absolute' }} />
                        )}
                    </div>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    {creatorUser && (
                        <WorksheetUser
                            label={dataTranslate.adminFormList.headers.creator}
                            avatar={{
                                firstName: creatorUser?.firstName,
                                lastName: creatorUser?.lastName,
                                imageUrl: 'https://picsum.photos/200/300',
                            }}
                            name={creatorUser?.name}
                            date={createdTime}
                            publicationStyle={{
                                fontFamily: 'Roboto',
                                fontWeight: '600',
                                fontSize: 18,
                                lineHeight: 24,
                                letterSpacing: 0,
                            }}
                        />
                    )}

                    {updatedUser && (
                        <WorksheetUser
                            label={dataTranslate.adminFormList.headers.lastEditor}
                            avatar={{
                                firstName: updatedUser?.firstName,
                                lastName: updatedUser?.lastName,
                                imageUrl: 'https://picsum.photos/200/300',
                            }}
                            name={updatedUser?.name || ''}
                            date={updatedTime}
                        />
                    )}
                </Col>
            </Row>

            <Row>
                <Col>
                    <div className="d-flex align-items-center">
                        <Label
                            style={{
                                fontFamily: 'Roboto',
                                fontWeight: '600',
                                marginTop: 8,
                                fontSize: 18,
                                lineHeight: 24,
                                letterSpacing: 0,
                            }}
                        >
                            {t('stisPublications:sideBarHeaders.versions', 'Versions')}
                        </Label>
                    </div>
                    <View>
                        {/* need to edit version */}
                        {parentPublications.length == 0 ? (
                            <>
                                <Typography
                                    color="blueOne"
                                    style={{
                                        fontFamily: 'Roboto',
                                        fontWeight: '600',
                                        fontSize: 15,
                                        lineHeight: 24,
                                        letterSpacing: 0,
                                    }}
                                >
                                    {withVersion(publicationInfo?.title, publicationInfo?.pubVersion)}
                                </Typography>

                                <div className={classes.versionMeta}>
                                    {!!createdTime && <Typography>{createdTime}</Typography>}
                                    <VersionStatus status={publicationInfo.status} />
                                </div>
                            </>
                        ) : (
                            <div>
                                {parentPublications.map((parent, index) => (
                                    <div style={{ marginTop: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography
                                                color="blueOne"
                                                style={{
                                                    fontFamily: 'Roboto',
                                                    fontWeight:
                                                        parent.documentId === publicationInfo.documentId
                                                            ? '600'
                                                            : '400',
                                                    fontSize: 15,
                                                    lineHeight: 24,
                                                    letterSpacing: 0,
                                                }}
                                            >
                                                {withVersion(
                                                    parent?.title,
                                                    parent.documentId === publicationInfo.documentId
                                                        ? publicationInfo.pubVersion
                                                        : parent.version
                                                )}
                                            </Typography>
                                            {index !== 0 && (
                                                <OverlayTrigger
                                                    key={index}
                                                    placement="bottom"
                                                    delay={{ show: 250, hide: 400 }}
                                                    overlay={
                                                        <Tooltip
                                                            id={`tooltip-${index}`}
                                                            className={classes.customTooltip}
                                                        >
                                                            Open version
                                                        </Tooltip>
                                                    }
                                                >
                                                    <Link
                                                        className={classes.icons}
                                                        style={{
                                                            cursor: 'pointer',
                                                        }}
                                                        to={`/administration/publication/${parent.documentId}?action=${StisPublicationAction.Edit}&status=${parent.status}`}
                                                        target="_blank"
                                                    >
                                                        <Icon name="open-in-new" color="blueOne" />
                                                    </Link>
                                                </OverlayTrigger>
                                            )}
                                        </div>

                                        <div className={classes.versionMeta} style={{ marginTop: -2 }}>
                                            {!!parent.createdDate && (
                                                <Typography>{getCurrentFormattedDate(parent.createdDate)}</Typography>
                                            )}
                                            <VersionStatus
                                                status={
                                                    parent.documentId === publicationInfo.documentId
                                                        ? publicationInfo.status
                                                        : parent.status
                                                }
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </View>
                </Col>
            </Row>
        </PublicationFormDetailViewAside>
    );
};
